/**
 * useOnlineImportHold — is importing online applications paused for this user?
 *
 * Bill 2026-10-07: hold legacy online application imports until entity, product,
 * disclosure and signing testing is complete ("limit the real customer blast
 * radius"). SystemSetting `onlineApplications.import.enabled` (seed 361) — a
 * missing or false value means PAUSED, matching the server (TestModeAccess,
 * fail closed). Test-mode users are refused regardless: online applicants are
 * real people. The server refuses the import either way; this only disables the
 * button and explains why.
 */
import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';

export const ONLINE_IMPORT_SETTING = 'onlineApplications.import.enabled';
export const IMPORT_PAUSED_NOTE = 'Importing online applications is paused while testing finishes';
export const IMPORT_TEST_MODE_NOTE =
  "You're in Test mode (test customers only), so online applications from real applicants can't be imported";

function truthy(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase());
  return false;
}

/** Null when importing is allowed; otherwise the plain note to show. */
export function useOnlineImportHold(): string | null {
  const { user } = useAuth();
  const { config } = useConfig();
  return useMemo(() => {
    const settings = config.systemSettings as Record<string, unknown> | undefined;
    if (!truthy(settings?.[ONLINE_IMPORT_SETTING])) return IMPORT_PAUSED_NOTE;
    if (user?.testMode?.active === true) return IMPORT_TEST_MODE_NOTE;
    return null;
  }, [config.systemSettings, user?.testMode?.active]);
}
